import React, { useState } from 'react';
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

const SearchAndFilters = ({
  searchTerm,
  setSearchTerm,
  selectedAnnee,
  setSelectedAnnee,
  selectedNiveau,
  setSelectedNiveau,
  selectedClasse,
  setSelectedClasse,
  annees,
  niveaux,
  classes,
  onClearFilters,
  onFiltersChange
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  // Compter les filtres actifs
  React.useEffect(() => {
    let count = 0;
    if (selectedAnnee) count++;
    if (selectedNiveau) count++;
    if (selectedClasse) count++;
    if (searchTerm) count++;
    setActiveFilters(count);
  }, [selectedAnnee, selectedNiveau, selectedClasse, searchTerm]);

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'annee':
        setSelectedAnnee(value);
        break;
      case 'niveau':
        setSelectedNiveau(value);
        break;
      case 'classe':
        setSelectedClasse(value);
        break;
      default:
        break;
    }
    
    if (onFiltersChange) {
      onFiltersChange();
    }
  };

  const clearAllFilters = () => {
    setSelectedAnnee('');
    setSelectedNiveau('');
    setSelectedClasse('');
    setSearchTerm('');
    if (onClearFilters) {
      onClearFilters();
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Recherche et Filtres</h3>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Masquer' : 'Afficher'} les filtres
            {activeFilters > 0 && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                {activeFilters}
              </span>
            )}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </button>
          
          {activeFilters > 0 && (
            <button
              onClick={clearAllFilters}
              className="flex items-center px-3 py-2 text-red-600 hover:text-red-700 transition-colors border border-red-200 rounded-lg hover:bg-red-50"
            >
              <X className="h-4 w-4 mr-2" />
              Effacer tout
            </button>
          )}
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une classe, un niveau ou un étudiant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors placeholder-gray-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filtres avancés */}
      {showFilters && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtre par année académique */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Année Académique
              </label>
              <select
                value={selectedAnnee}
                onChange={(e) => handleFilterChange('annee', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors bg-white"
              >
                <option value="">Toutes les années</option>
                {annees.map((annee) => (
                  <option key={annee.id} value={annee.id}>
                    {annee.annee_debut}-{annee.annee_fin}
                    {annee.status && ' (Active)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre par niveau */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Niveau
              </label>
              <select
                value={selectedNiveau}
                onChange={(e) => handleFilterChange('niveau', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors bg-white"
              >
                <option value="">Tous les niveaux</option>
                {niveaux.map((niveau) => (
                  <option key={niveau.id} value={niveau.id}>
                    {niveau.libelle}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre par classe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Classe
              </label>
              <select
                value={selectedClasse}
                onChange={(e) => handleFilterChange('classe', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors bg-white"
              >
                <option value="">Toutes les classes</option>
                {classes.map((classe) => (
                  <option key={classe.id} value={classe.id}>
                    {classe.libelle} - {classe.niveau?.libelle || 'Niveau inconnu'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Résumé des filtres actifs */}
          {activeFilters > 0 && (
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>Filtres actifs:</span>
                {selectedAnnee && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                    Année: {annees.find(a => a.id === parseInt(selectedAnnee))?.annee_debut}-{annees.find(a => a.id === parseInt(selectedAnnee))?.annee_fin}
                  </span>
                )}
                {selectedNiveau && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    Niveau: {niveaux.find(n => n.id === parseInt(selectedNiveau))?.libelle}
                  </span>
                )}
                {selectedClasse && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                    Classe: {classes.find(c => c.id === parseInt(selectedClasse))?.libelle}
                  </span>
                )}
                {searchTerm && (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                    Recherche: "{searchTerm}"
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAndFilters;
